/**
 * cron 표현식을 사람이 읽을 수 있게 푼다.
 *
 * 배치관리 화면은 `0,10,20,30,40,50 * * * *` 같은 문자열을 그대로 보여줬다.
 * 크론을 읽을 줄 아는 사람만 "10분마다"인지 알 수 있고, **다음에 언제 도는지**는
 * 아무도 알 수 없었다 - 켜 두고 한참 뒤에야 "왜 안 돌지?"를 확인하게 된다.
 *
 * 서버가 아니라 화면에서 푼다. 표시용 값이고, 서버는 이미 형식만 검사한다
 * (crontab.logic.ts 의 isValidSchedule - 값의 뜻까지는 보지 않는다).
 */

const FIELD_RANGES: [number, number][] = [
  [0, 59], // 분
  [0, 23], // 시
  [1, 31], // 일
  [1, 12], // 월
  [0, 6], // 요일 (0=일)
];

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'] as const;

/**
 * 한 칸이 허용하는 값들. 형식이 틀리면 null.
 *
 * 별표, 별표-슬래시-n(간격), a-b(범위), a,b,c(나열)와 숫자를 받는다 -
 * crontab.logic.ts 의
 * isValidSchedule 이 통과시키는 글자(`0-9 * / , -`)와 같은 범위다.
 */
export function expandField(field: string, index: number): number[] | null {
  const range = FIELD_RANGES[index];
  if (!range) return null;
  const [min, max] = range;

  const out = new Set<number>();
  for (const part of field.split(',')) {
    if (part === '') return null;

    const [spec, stepText] = part.split('/') as [string, string | undefined];
    let step = 1;
    if (stepText !== undefined) {
      step = Number(stepText);
      if (!Number.isInteger(step) || step < 1) return null;
    }

    let from: number;
    let to: number;
    if (spec === '*') {
      from = min;
      to = max;
    } else if (spec.includes('-')) {
      const [a, b] = spec.split('-') as [string, string | undefined];
      from = Number(a);
      to = Number(b);
      if (!Number.isInteger(from) || !Number.isInteger(to) || from > to) return null;
    } else {
      from = Number(spec);
      to = from;
      if (!Number.isInteger(from)) return null;
    }
    if (from < min || to > max) return null;

    for (let v = from; v <= to; v += step) out.add(v);
  }

  return [...out].sort((a, b) => a - b);
}

export interface CronFields {
  minute: number[];
  hour: number[];
  day: number[];
  month: number[];
  weekday: number[];
}

export function parseCron(schedule: string): CronFields | null {
  const parts = schedule.trim().split(/\s+/).filter(Boolean);
  if (parts.length !== 5) return null;

  const fields = parts.map((p, i) => expandField(p, i));
  if (fields.some((f) => f === null || f.length === 0)) return null;

  const [minute, hour, day, month, weekday] = fields as number[][];
  return { minute: minute!, hour: hour!, day: day!, month: month!, weekday: weekday! };
}

/** 0,10,20,30,40,50 처럼 간격이 일정한지 - "10분마다"로 줄여 쓰기 위해. */
function evenStep(values: number[], min: number, max: number): number | null {
  if (values.length < 2) return null;
  const step = values[1]! - values[0]!;
  if (step < 2) return null;
  for (let i = 1; i < values.length; i++) {
    if (values[i]! - values[i - 1]! !== step) return null;
  }
  // 처음이 범위 시작이고 마지막에서 한 칸 더 가면 범위를 넘어야 "매 n"이다.
  if (values[0] !== min) return null;
  if (values[values.length - 1]! + step <= max) return null;
  return step;
}

const two = (n: number) => String(n).padStart(2, '0');

/**
 * 사람이 읽는 한 줄. 못 푸는 표현식이면 null 을 돌려주고, 화면은 원문만 보여준다.
 *
 * 지금 카탈로그의 20개를 모두 문장으로 만들 수 있는지는 테스트로 고정한다.
 */
export function describeCron(schedule: string): string | null {
  const f = parseCron(schedule);
  if (!f) return null;

  const everyMinute = f.minute.length === 60;
  const everyHour = f.hour.length === 24;
  const everyDay = f.day.length === 31;
  const everyMonth = f.month.length === 12;
  const everyWeekday = f.weekday.length === 7;

  // 시각 부분
  let time: string;
  const minuteStep = evenStep(f.minute, 0, 59);
  if (everyMinute) {
    time = '1분마다';
  } else if (minuteStep !== null && everyHour) {
    time = `${minuteStep}분마다`;
  } else if (everyHour) {
    time = `매시 ${f.minute.map(two).join(',')}분`;
  } else if (f.minute.length === 1) {
    const hours = f.hour.map((h) => `${two(h)}시`).join(', ');
    time = `${hours} ${two(f.minute[0]!)}분`;
  } else {
    time = `${f.hour.map(two).join(',')}시의 ${f.minute.map(two).join(',')}분`;
  }

  // 날짜 부분
  const scope: string[] = [];
  if (!everyMonth) scope.push(`${f.month.join(',')}월`);
  if (!everyDay) scope.push(`${f.day.join(',')}일`);
  if (!everyWeekday) scope.push(`${f.weekday.map((d) => WEEKDAYS[d] ?? d).join('·')}요일`);

  return scope.length > 0 ? `${scope.join(' ')} ${time}` : time;
}

/**
 * 다음 실행 시각.
 *
 * 하루 단위로 먼저 걸러 넘기고(월/일/요일), 맞는 날에서만 시/분을 본다 -
 * 1분씩 훑으면 "1월 1일 0시"처럼 드문 표현식에서 50만 번을 돌게 된다.
 * 2년 안에 맞는 날이 없으면 null (`31 2 * *` 같은 것).
 *
 * cron 은 **일과 요일을 둘 다 지정하면 OR** 로 본다(둘 중 하나만 맞아도 실행).
 * 한쪽이 `*` 면 나머지만 본다 - 이 규칙을 잘못 옮기면 실행 시각이 통째로 어긋난다.
 */
export function nextRun(schedule: string, from: Date = new Date()): Date | null {
  const f = parseCron(schedule);
  if (!f) return null;

  const dayRestricted = f.day.length !== 31;
  const weekdayRestricted = f.weekday.length !== 7;
  const dayMatches = (d: Date): boolean => {
    if (!f.month.includes(d.getMonth() + 1)) return false;
    const byDay = f.day.includes(d.getDate());
    const byWeekday = f.weekday.includes(d.getDay());
    if (dayRestricted && weekdayRestricted) return byDay || byWeekday;
    if (dayRestricted) return byDay;
    if (weekdayRestricted) return byWeekday;
    return true;
  };

  // 다음 분부터 본다 - 지금 이 분은 이미 지나갔다고 친다.
  const start = new Date(from.getTime());
  start.setSeconds(0, 0);
  start.setMinutes(start.getMinutes() + 1);

  const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  for (let i = 0; i < 366 * 2; i++) {
    if (dayMatches(cursor)) {
      for (const h of f.hour) {
        for (const m of f.minute) {
          const at = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate(), h, m);
          if (at >= start) return at;
        }
      }
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return null;
}

/** '3분 뒤', '2시간 뒤' 처럼 짧게. 표에서 다음 실행까지 남은 시간을 보여준다. */
export function untilText(target: Date, from: Date = new Date()): string {
  const diffMin = Math.round((target.getTime() - from.getTime()) / 60_000);
  if (diffMin < 1) return '곧';
  if (diffMin < 60) return `${diffMin}분 뒤`;
  const hours = Math.floor(diffMin / 60);
  if (hours < 24) return `${hours}시간 뒤`;
  return `${Math.floor(hours / 24)}일 뒤`;
}
